// What every online card game shares in the browser (.ai/multiplayer-protocol-v2.md "Card games"): the frames, the
// face-down stand-ins for the cards a seat may not see, the seat names (the players' own, drawn ones for the bots),
// and the turn clock. A game's online table is one file beside this one (makao.ts, hearts.ts), registered in
// index.ts; its store has the offline store's interface, so the game's table view works unchanged.
import { useEffect, useState } from 'react';
import { t } from '../i18n';
import { drawNames, fixedNames, initial, nameList, type SeatNames } from '../hearts/names';
import type { HCard } from '../hearts/rules';

/** A player of `game.start`, turned (index 0 = me); a bot seat is `{bot: true}`. */
export type CardPlayer = { name: string; flag?: string } | { bot: true };

export interface CardStart {
  game: string;
  variant: string;
  /** My seat at the table (the record only: every game frame is turned so I am seat 0). */
  you: number;
  players: CardPlayer[];
  /** The same at every seat: the bots get the same names in every browser (in one language). */
  seed?: string;
  /** Milliseconds since the game began, by the server's clock: 0 at a new game, more when the page comes back to a running
   *  one (a dropped line, a reload). Missing from an older server. */
  elapsed?: number;
}

/** The table's clock at begin() from the server's time (owner 2026-11-02: after a dropped line the clock went back to 0;
 *  the start frame comes again then): the seconds shown and when the game began (the short-game rule reads it). */
export function clockOf(start: CardStart): { seconds: number; startedAt: number } {
  const ms = Math.max(0, Number(start.elapsed) || 0);
  return { seconds: Math.floor(ms / 1000), startedAt: Date.now() - ms };
}

export interface CardStateFrame {
  /** The rules' state turned for me, the hidden parts as counts (the game's file expands them). */
  state: Record<string, unknown>;
  last?: Record<string, unknown> & { seat: number };
  /** ms left for my own move; then the server's bot plays it for me. */
  deadline?: number;
  /** The other players on turn (2026-11-02): their seat (turned, as `state`), ms left, ms they have thought already. */
  turns?: Array<{ seat: number; left: number; waited: number }>;
}

/** A player on turn is shown as awaited once they have thought this long (owner 2026-11-02: "mniej więcej 5 s po jego
 *  bezczynności"; a long think looked like a stuck game). */
export const WAIT_SHOW_MS = 5000;
/** The other players we wait for, by seat: when their thinking began and when their time runs out (ms epoch). Set from
 *  every game.state (app-entry.tsx), cleared when the online game starts, ends or stops; offline it stays empty. */
let seatWaits: ReadonlyMap<number, { since: number; at: number }> = new Map();
const waitListeners = new Set<() => void>();
export function setSeatWaits(turns: CardStateFrame['turns']): void {
  const now = Date.now();
  seatWaits = new Map((turns ?? []).map((w) => [w.seat, { since: now - w.waited, at: now + w.left }]));
  for (const fn of waitListeners) fn();
}

/** The seconds left of `seat`'s move, counting down to 0, once they have thought WAIT_SHOW_MS; else null (not on turn,
 *  not long yet). Owner 2026-11-02: "zegar pojawia się po 5 s i liczy do zera (w dół)". */
export function useSeatWait(seat: number): number | null {
  const [, redraw] = useState(0);
  useEffect(() => {
    const fn = () => redraw((n) => n + 1);
    waitListeners.add(fn);
    return () => {
      waitListeners.delete(fn);
    };
  }, []);
  const w = seatWaits.get(seat);
  useEffect(() => {
    if (!w) return undefined;
    const id = window.setInterval(() => redraw((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [w]);
  if (!w) return null;
  const now = Date.now();
  return now - w.since < WAIT_SHOW_MS ? null : Math.max(0, Math.ceil((w.at - now) / 1000));
}

export interface CardEndFrame {
  game: string;
  winners: number[];
  scores?: number[];
  players: CardPlayer[];
}

/** The card games' end and score windows. */
export type CardWindow = 'makao_end' | 'hearts_score' | 't1k_score' | 't58_score';
export const CARD_WINDOWS: readonly CardWindow[] = ['makao_end', 'hearts_score', 't1k_score', 't58_score'];

/** What the app gives a game's online table: the way to the server, and the app's windows. */
export interface OnlineDeps {
  send(move: Record<string, unknown>): void;
  openWindow(name: CardWindow): void;
  closeWindow(name: CardWindow): void;
  onStep?(what: string): void;
}

/** One online card game: its store goes in place of the offline one from begin() to stop(). */
export interface OnlineCardGame {
  readonly game: string;
  begin(start: CardStart): void;
  state(f: CardStateFrame): void;
  end(f: CardEndFrame): void;
  /** The player left the table: the offline store comes back. */
  stop(): void;
  /** A seat's player left or dropped off (a bot plays it: `bot`), or came back, or a newcomer took a bot's seat: its
   *  name and badge change (`flag`: the player's country, '' for a guest). */
  rename(seat: number, name: string, bot: boolean, flag?: string): void;
}

/** The table's names after a seat changed hands: the player's name, with a robot badge while a bot plays it
 *  (`benched`, online/SeatAvatar.tsx; owner 2026-11-02: no "(bot)" after the name, it doubled the brackets in
 *  sentences), their own badge again after. */
export function renamed(names: SeatNames | null, seat: number, name: string, bot: boolean, flag?: string): SeatNames | null {
  if (!names || seat <= 0) return names;
  const name2 = names.name.slice();
  const benched = (names.benched ?? names.name.map(() => false)).slice();
  name2[seat] = name;
  benched[seat] = bot;
  // a person at the seat (back, or a newcomer in a bot's seat, owner 2026-11-02: no badge came): their flag, or the
  // guest's person badge (''); a bot seat at the start had none (null)
  const flags = names.flags?.slice();
  if (flags && !bot && flag !== undefined) flags[seat] = flag;
  return { ...names, name: name2, from: name2, benched, ...(flags ? { flags } : {}) };
}

/** A card the seat may not see (the others' hands, the stock): drawn face down, never named. */
export const HIDDEN: HCard = { s: 'S', r: 0 };

/** A hand from a frame: my cards as they are, a count as that many face-down stand-ins. */
export const expand = (h: unknown): HCard[] => (Array.isArray(h) ? (h as HCard[]) : Array.from({ length: Number(h) || 0 }, () => HIDDEN));

/** A random source that gives the same numbers for the same seed (FNV-1a, then mulberry32). */
export function seeded(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return () => {
    h = (h + 0x6d2b79f5) | 0;
    let x = Math.imul(h ^ (h >>> 15), 1 | h);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/** Every seat's name by table seat: the player's own, a drawn one for a bot (the same in every browser for the table's
 *  seed `id:since`; the lobby's running games name the bots the same way, 2026-10-30). */
export function namesBySeat(atSeat: readonly CardPlayer[], seed: string | undefined): string[] {
  const drawn = drawNames(seed ? seeded(seed) : Math.random, nameList(t('hearts.names')), '') ?? fixedNames(t);
  const used = new Set(atSeat.flatMap((p) => ('name' in p ? [initial(p.name)] : [])));
  const free = drawn.name.slice(1).filter((x) => !used.has(initial(x)));
  let next = 0;
  return atSeat.map((p, abs) => ('name' in p ? p.name : (free[next++] ?? drawn.name[1 + (abs % 3)]!)));
}

/** Seat names for the table: the players' own (index 0 is "you" in the views), a drawn name for each bot. The bots'
 *  names come from the table's seed and go out by table seat, so every browser (in one language) names them alike. */
export function seatNamesFor(start: Pick<CardStart, 'players' | 'you' | 'seed'>): SeatNames {
  const { players, you, seed } = start;
  const n = players.length;
  // players[k] sits at table seat (k + you) % n
  const bySeat = namesBySeat(Array.from({ length: n }, (_, abs) => players[(abs - you + n) % n]!), seed);
  const name = players.map((_, k) => (k === 0 ? '' : bySeat[(k + you) % n]!));
  const flags = players.map((p) => ('name' in p ? (p.flag ?? '') : null));
  return { name, from: name, flags };
}

/** The seconds left of my move, for the table's hint; null when it is not my move. */
export function useTurnSeconds(deadlineAt: number | null | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadlineAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [deadlineAt]);
  return deadlineAt ? Math.max(0, Math.ceil((deadlineAt - now) / 1000)) : null;
}
